import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Container, Typography, Paper, TextField, Button, Autocomplete,
  Dialog, DialogTitle, DialogContent, DialogActions,
  MenuItem, Select, FormControl, InputLabel,
  Chip, IconButton, Checkbox, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Toolbar, Tooltip
} from '@mui/material';
import { Delete, Edit, Add, Search, PictureAsPdf, Print } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import jsPDF from 'jspdf';

const KBPage = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('All');
  const [selected, setSelected] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentPost, setCurrentPost] = useState({ category: '', title: '', content: '' });

  const fetchCategories = useCallback(async () => {
    try {
      const response = await api.get('/kb/categories');
      setCategories(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Failed to fetch categories', err);
      setCategories([]);
    }
  }, []);

  const fetchPosts = useCallback(async () => {
    try {
      const response = await api.get('/kb', {
        params: {
          search,
          category: filterCategory === 'All' ? '' : filterCategory,
        }
      });
      setPosts(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Failed to fetch posts', err);
      setPosts([]);
    }
  }, [search, filterCategory]);

  useEffect(() => {
    fetchCategories();
    fetchPosts();
  }, [fetchCategories, fetchPosts]);

  const handleOpenDialog = (post = null) => {
    if (post) {
      setIsEditing(true);
      setCurrentPost(post);
    } else {
      setIsEditing(false);
      setCurrentPost({ category: '', title: '', content: '' });
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => setIsDialogOpen(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditing) {
        await api.put(`/kb/${currentPost.id}`, currentPost);
      } else {
        await api.post('/kb', currentPost);
      }
      await fetchPosts();
      await fetchCategories();
      handleCloseDialog();
    } catch (err) {
      alert(err.message || 'Save failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    try {
      await api.delete(`/kb/${id}`);
      setSelected(prev => prev.filter(sid => sid !== id));
      await fetchPosts();
    } catch (err) {
      alert(err.message || 'Delete failed');
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelected(posts.map(p => p.id));
    } else {
      setSelected([]);
    }
  };

  const handleSelectOne = (id) => {
    setSelected(prev =>
      prev.includes(id) ? prev.filter(sid => sid !== id) : [...prev, id]
    );
  };

  const getSelectedPosts = () => posts.filter(p => selected.includes(p.id));

  const handleExportPDF = () => {
    const doc = new jsPDF();
    const selectedPosts = getSelectedPosts();

    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Knowledge Base', 14, 18);

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(120);
    doc.text(`Exported: ${new Date().toLocaleString()}  |  ${selectedPosts.length} post(s)`, 14, 26);

    doc.setDrawColor(180);
    doc.line(14, 30, 196, 30);

    let y = 38;

    selectedPosts.forEach((post, index) => {
      if (y > 255) {
        doc.addPage();
        y = 20;
      }

      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0);
      const titleLines = doc.splitTextToSize(`${index + 1}. ${post.title}`, 180);
      doc.text(titleLines, 14, y);
      y += titleLines.length * 6 + 2;

      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100);
      doc.text(
        `Category: ${post.category}   |   By: ${post.author_name}   |   ${new Date(post.created_at).toLocaleDateString()}`,
        14, y
      );
      y += 7;

      doc.setFontSize(10);
      doc.setTextColor(40);
      const contentLines = doc.splitTextToSize(post.content, 180);
      contentLines.forEach(line => {
        if (y > 275) {
          doc.addPage();
          y = 20;
        }
        doc.text(line, 14, y);
        y += 5;
      });
      y += 4;

      if (index < selectedPosts.length - 1) {
        doc.setDrawColor(210);
        doc.line(14, y, 196, y);
        y += 8;
      }
    });

    doc.save('knowledge-base.pdf');
  };

  const handlePrint = () => {
    const selectedPosts = getSelectedPosts();
    const rows = selectedPosts.map((post, index) => `
      <div class="post${index < selectedPosts.length - 1 ? ' divider' : ''}">
        <h2>${post.title}</h2>
        <p class="meta">
          <span class="chip">${post.category}</span>
          &nbsp;&bull;&nbsp;By ${post.author_name}
          &nbsp;&bull;&nbsp;${new Date(post.created_at).toLocaleDateString()}
        </p>
        <p class="content">${post.content.replace(/\n/g, '<br>')}</p>
      </div>
    `).join('');

    const win = window.open('', '_blank');
    win.document.write(`<!DOCTYPE html><html><head>
      <title>Knowledge Base</title>
      <style>
        body { font-family: Arial, sans-serif; max-width: 780px; margin: 0 auto; padding: 24px; color: #222; }
        h1 { font-size: 22px; margin-bottom: 2px; }
        .date { color: #777; font-size: 11px; margin-bottom: 20px; }
        .post { margin-bottom: 18px; }
        .divider { border-bottom: 1px solid #ddd; padding-bottom: 18px; margin-bottom: 18px; }
        h2 { font-size: 16px; margin: 0 0 4px 0; }
        .meta { font-size: 11px; color: #666; margin: 0 0 8px 0; }
        .chip { background: #e3f2fd; color: #1565c0; padding: 1px 8px; border-radius: 10px; }
        .content { font-size: 13px; line-height: 1.7; margin: 0; }
        @media print { body { padding: 0; } }
      </style>
    </head><body>
      <h1>Knowledge Base</h1>
      <p class="date">Printed: ${new Date().toLocaleString()} &nbsp;&bull;&nbsp; ${selectedPosts.length} post(s)</p>
      ${rows}
    </body></html>`);
    win.document.close();
    win.print();
  };

  const numSelected = selected.length;
  const numPosts = posts.length;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography variant="h3">Knowledge Base</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}>
          Create Post
        </Button>
      </Box>

      {/* Search & Filter */}
      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          label="Search KB..."
          variant="outlined"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} /> }}
          sx={{ flexGrow: 1 }}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Category</InputLabel>
          <Select
            value={filterCategory}
            label="Category"
            onChange={(e) => setFilterCategory(e.target.value)}
          >
            <MenuItem value="All">All Categories</MenuItem>
            {(Array.isArray(categories) ? categories : []).map(cat => (
              <MenuItem key={cat} value={cat}>{cat}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      {/* Toolbar with select-all and actions */}
      <Paper sx={{ mb: 0, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}>
        <Toolbar
          variant="dense"
          sx={{
            pl: 1, pr: 2, gap: 1,
            bgcolor: numSelected > 0 ? 'primary.50' : 'grey.50',
            borderBottom: '1px solid', borderColor: 'divider',
          }}
        >
          <Checkbox
            size="small"
            indeterminate={numSelected > 0 && numSelected < numPosts}
            checked={numPosts > 0 && numSelected === numPosts}
            onChange={handleSelectAll}
          />
          <Typography sx={{ flex: 1 }} variant="body2" color={numSelected > 0 ? 'primary' : 'text.secondary'}>
            {numSelected > 0 ? `${numSelected} of ${numPosts} selected` : `${numPosts} post${numPosts !== 1 ? 's' : ''}`}
          </Typography>
          <Tooltip title={numSelected === 0 ? 'Select posts to export' : 'Export selected to PDF'}>
            <span>
              <Button
                size="small"
                startIcon={<PictureAsPdf />}
                onClick={handleExportPDF}
                disabled={numSelected === 0}
                variant={numSelected > 0 ? 'contained' : 'outlined'}
                color="error"
                sx={{ mr: 1 }}
              >
                Export PDF
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={numSelected === 0 ? 'Select posts to print' : 'Print selected'}>
            <span>
              <Button
                size="small"
                startIcon={<Print />}
                onClick={handlePrint}
                disabled={numSelected === 0}
                variant={numSelected > 0 ? 'contained' : 'outlined'}
              >
                Print
              </Button>
            </span>
          </Tooltip>
        </Toolbar>
      </Paper>

      {/* List view */}
      <TableContainer component={Paper} sx={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell padding="checkbox" />
              <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Author</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {numPosts === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                  No posts found
                </TableCell>
              </TableRow>
            ) : (
              posts.map(post => {
                const isItemSelected = selected.includes(post.id);
                return (
                  <TableRow
                    key={post.id}
                    hover
                    selected={isItemSelected}
                    onClick={() => handleSelectOne(post.id)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={isItemSelected}
                        onClick={e => e.stopPropagation()}
                        onChange={() => handleSelectOne(post.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{post.title}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {post.content.length > 100 ? `${post.content.substring(0, 100)}…` : post.content}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={post.category} size="small" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{post.author_name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(post.created_at).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" onClick={e => e.stopPropagation()}>
                      <IconButton onClick={() => handleOpenDialog(post)} color="primary" size="small">
                        <Edit fontSize="small" />
                      </IconButton>
                      {user?.role_id === 1 && (
                        <IconButton onClick={() => handleDelete(post.id)} color="error" size="small">
                          <Delete fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create / Edit dialog */}
      <Dialog open={isDialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle>{isEditing ? 'Edit Post' : 'Create KB Post'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent dividers>
            <TextField
              fullWidth label="Title" margin="normal" required
              value={currentPost.title}
              onChange={(e) => setCurrentPost({ ...currentPost, title: e.target.value })}
            />
            <Autocomplete
              freeSolo
              options={categories}
              value={currentPost.category}
              onChange={(event, newValue) => setCurrentPost({ ...currentPost, category: newValue || '' })}
              onInputChange={(event, newInputValue) => setCurrentPost({ ...currentPost, category: newInputValue })}
              renderInput={(params) => (
                <TextField {...params} label="Category" margin="normal" required />
              )}
            />
            <TextField
              fullWidth label="Content / Suggestion" margin="normal" required
              multiline rows={4}
              value={currentPost.content}
              onChange={(e) => setCurrentPost({ ...currentPost, content: e.target.value })}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained">Save</Button>
          </DialogActions>
        </form>
      </Dialog>
    </Container>
  );
};

export default KBPage;
